import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";
import { searchApi } from "@/lib/api";
import { Building2, FolderKanban, CheckSquare, Users, StickyNote, Calendar } from "lucide-react";

export default function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults(null); return; }
  }, [open]);

  useEffect(() => {
    let alive = true;
    if (q.trim().length < 2) { setResults(null); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await searchApi.global(q);
        if (alive) setResults(r.data);
      } catch {}
    }, 220);
    return () => { alive = false; clearTimeout(timer); };
  }, [q]);

  const go = (path) => { setOpen(false); nav(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <div data-testid="global-search-dialog">
        <CommandInput placeholder="Search entities, projects, tasks, contacts, notes, events…" value={q} onValueChange={setQ} data-testid="global-search-input" />
        <CommandList className="max-h-[480px]">
          <CommandEmpty>{q.length < 2 ? "Type at least 2 characters." : "No results."}</CommandEmpty>
          {results?.entities?.length > 0 && (
            <CommandGroup heading="Entities">
              {results.entities.map((e) => (
                <CommandItem key={e.id} onSelect={() => go(`/entity/${e.id}`)}>
                  <Building2 className="w-4 h-4 mr-2 text-white/50" /> {e.name}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.projects?.length > 0 && (
            <CommandGroup heading="Projects">
              {results.projects.map((p) => (
                <CommandItem key={p.id} onSelect={() => go(`/entity/${p.entity_id}?tab=projects`)}>
                  <FolderKanban className="w-4 h-4 mr-2 text-white/50" /> {p.name}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.tasks?.length > 0 && (
            <CommandGroup heading="Tasks">
              {results.tasks.map((t) => (
                <CommandItem key={t.id} onSelect={() => go(`/tasks`)}>
                  <CheckSquare className="w-4 h-4 mr-2 text-white/50" /> {t.name}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.contacts?.length > 0 && (
            <CommandGroup heading="Contacts">
              {results.contacts.map((c) => (
                <CommandItem key={c.id} onSelect={() => go(`/contacts/${c.id}`)}>
                  <Users className="w-4 h-4 mr-2 text-white/50" /> {c.full_name}{c.email ? ` — ${c.email}` : ""}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.notes?.length > 0 && (
            <CommandGroup heading="Notes & Decisions">
              {results.notes.map((n) => (
                <CommandItem key={n.id} onSelect={() => go(`/entity/${n.entity_id}?tab=notes`)}>
                  <StickyNote className="w-4 h-4 mr-2 text-white/50" /> {n.title}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.events?.length > 0 && (
            <CommandGroup heading="Calendar Events">
              {results.events.map((ev) => (
                <CommandItem key={ev.id} onSelect={() => go(`/calendar`)}>
                  <Calendar className="w-4 h-4 mr-2 text-white/50" /> {ev.title}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </div>
    </CommandDialog>
  );
}
